/**
 * Build phase message translations.
 *
 * Maps machine-readable message IDs (emitted by the backend pipeline)
 * to user-facing display strings. This is the single source of truth
 * for all build progress messages shown in the UI.
 *
 * Message IDs are SCREAMING_SNAKE_CASE constants defined on each
 * PipelineStep's `phase_message_id` field in the backend.
 */

/** Semantic model build phases */
const SEMANTIC_MESSAGES: Record<string, string> = {
  LOADING_DATA: 'Loading your data…',
  UNDERSTANDING_STRUCTURE: 'Understanding your data…',
  DISCOVERING_RELATIONSHIPS: 'Discovering relationships…',
  DISCOVERING_FILTERS: 'Preparing filter suggestions…',
  RANKING_FILTERS: 'Ranking filter options…',
  SELECTING_VIEWS: 'Identifying key business metrics…',
  SELECTING_VIEWS_ALG: 'Organizing data views…',
  DESCRIBING_VIEWS: 'Describing your data views…',
  VALIDATING_DATA: 'Validating data quality…',
  ADDING_DERIVED_COLUMNS: 'Adding derived fields…',
  LABELING_COLUMNS: 'Understanding your columns…',
  PROPOSING_COLUMNS: 'Suggesting calculated fields…',
  CURATING_VIEWS: 'Building business views…',
  ADDING_TIME_FIELDS: 'Adding time-based fields…',
  OPTIMIZING_TYPES: 'Optimizing data types…',
  ANALYZING_CONNECTIONS: 'Analyzing data connections…',
  BUILDING_METRICS: 'Finding patterns and metrics…',
  SAVING_MODEL: 'Saving your model…',
  GENERATING_INSIGHTS: 'Discovering insights…',
  PROFILING_VIEWS: 'Profiling your data…',
};

/** Dashboard generation phases */
const DASHBOARD_MESSAGES: Record<string, string> = {
  PREPARING_DATA: 'Preparing your data…',
  ANALYZING_DATA: 'Analyzing your data…',
  DESIGNING_DASHBOARD: 'Designing your dashboard…',
  BUILDING_CHARTS: 'Building your charts…',
  POLISHING_DASHBOARD: 'Polishing your dashboard…',
  VALIDATING_SQL: 'Validating queries…',
  SAVING_DASHBOARD: 'Saving your dashboard…',
  FINAL_CHECKS: 'Running final checks…',
};

/** All message translations merged */
export const BUILD_PHASE_MESSAGES: Record<string, string> = {
  ...SEMANTIC_MESSAGES,
  ...DASHBOARD_MESSAGES,
};

/**
 * Translate a build phase message ID to a user-facing string.
 *
 * Resolution order:
 * 1. `messageId` lookup in BUILD_PHASE_MESSAGES
 * 2. Raw `fallbackMessage` (legacy phase_message from backend)
 * 3. Default generic message
 */
export function translateBuildPhase(
  messageId: string | undefined | null,
  fallbackMessage?: string | null,
): string {
  if (messageId && BUILD_PHASE_MESSAGES[messageId]) {
    return BUILD_PHASE_MESSAGES[messageId];
  }
  if (fallbackMessage) {
    return fallbackMessage;
  }
  return 'AI is analyzing your data and building the semantic model…';
}